import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useAIService } from '@/context/AIServiceContext';
import sanitizeText from '@/utils/sanitizeText';
import { 
  FaRobot, 
  FaCheckCircle, 
  FaExclamationTriangle,
  FaLightbulb,
  FaSync,
  FaClipboard,
  FaTimes,
  FaInfoCircle,
  FaDownload,
  FaTools
} from 'react-icons/fa';
import './RouterLLMValidator.scss';
import * as Sentry from '@sentry/react';

/**
 * RouterLLMValidator Component
 * 
 * Provides AI-powered router configuration review (similar to GitHub Copilot code reviews).
 * 
 * Features:
 * - Analyzes RouterOS configuration scripts for potential issues
 * - Provides recommendations for improvements
 * - Identifies security concerns and best practice violations
 * - Suggests optimizations and enhancements
 * - Categorizes findings by severity (Critical, High, Medium, Low, Info)
 * 
 * **IMPORTANT**: This component respects the "Enable AI Insights" setting
 * from the AI configuration. If AI is disabled, it shows a message instead
 * of the validation form.
 * 
 * API Endpoint:
 * - POST /api/RouterConfig/validate-with-llm
 * 
 * Use Cases:
 * - Pre-deployment config review
 * - Security audit of existing configurations
 * - Best practice compliance check
 * - Learning and improving router management skills
 */
const RouterLLMValidator = ({ routerId, routerName }) => {
  const { apiFetch } = useAuth();
  const { aiEnabled, isLoading: aiLoading } = useAIService();

  // State management
  const [loading, setLoading] = useState(false);
  const [fetchingConfig, setFetchingConfig] = useState(false);
  const [error, setError] = useState(null);
  const [configuration, setConfiguration] = useState('');
  const [validationResult, setValidationResult] = useState(null);

  // Auto-address / apply fix state
  const [autoAddressingIndex, setAutoAddressingIndex] = useState(null);
  const [applyingRecIndex, setApplyingRecIndex] = useState(null);
  const [generatedFix, setGeneratedFix] = useState(null);
  const [applyingFix, setApplyingFix] = useState(false);
  const [fixError, setFixError] = useState(null);
  const [fixSuccess, setFixSuccess] = useState(null);

  /**
   * Fetch live configuration from the router via API and analyse it directly
   */
  const handleFetchAndValidate = async () => {
    if (!routerId) {
      setError('No router ID available. Open this from a router detail page.');
      return;
    }

    setFetchingConfig(true);
    setError(null);
    setValidationResult(null);

    try {
      const response = await apiFetch(`/api/RouterConfig/${routerId}/validate-live-config?includeKnowledgeBase=true`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch and validate router configuration');
      }

      const data = await response.json();
      if (data.success) {
        setValidationResult(data.data);
      } else {
        throw new Error(data.error || 'Failed to validate configuration');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error fetching and validating configuration:', err);
      setError(err.message || 'Failed to fetch and validate configuration');
    } finally {
      setFetchingConfig(false);
    }
  };

  /**
   * Handle paste from clipboard
   */
  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setConfiguration(text);
    } catch (err) {
        Sentry.captureException(err);
      console.error('Failed to read clipboard:', err);
      setError('Failed to read from clipboard. Please paste manually.');
    }
  };

  /**
   * Handle configuration validation
   */
  const handleValidate = async () => {
    if (!configuration.trim()) {
      setError('Please enter a configuration to validate');
      return;
    }

    setLoading(true);
    setError(null);
    setValidationResult(null);

    try {
      const response = await apiFetch('/api/RouterConfig/validate-with-llm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ configuration: configuration.trim() })
      });

      if (!response.ok) {
        throw new Error('Failed to validate configuration');
      }

      const data = await response.json();
      if (data.success) {
        setValidationResult(data.data);
      } else {
        throw new Error(data.error || 'Failed to validate configuration');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error validating configuration:', err);
      setError(err.message || 'Failed to validate configuration');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Clear all data and start fresh
   */
  const handleClear = () => {
    setConfiguration('');
    setValidationResult(null);
    setError(null);
  };

  /**
   * Get severity badge class
   */
  const getSeverityClass = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'severity-critical';
      case 'high':
        return 'severity-high';
      case 'medium':
        return 'severity-medium';
      case 'low':
        return 'severity-low';
      default:
        return 'severity-info';
    }
  };

  /**
   * Get severity icon
   */
  const getSeverityIcon = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
      case 'high':
        return <FaExclamationTriangle />;
      case 'medium':
      case 'low':
        return <FaInfoCircle />;
      default:
        return <FaLightbulb />;
    }
  };

  /**
   * Generate RouterOS fix commands for a validation issue via LLM
   */
  const handleAutoAddress = async (issue, index) => {
    setAutoAddressingIndex(index);
    setFixError(null);
    setFixSuccess(null);
    try {
      const response = await apiFetch(`/api/RouterConfig/${routerId}/generate-fix-commands`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: issue.title || issue.message || '',
          description: issue.description || '',
          recommendation: issue.recommendation || '',
          category: issue.category || 'general',
          severity: issue.severity || 'info'
        })
      });
      const data = await response.json();
      if (data.success) {
        setGeneratedFix({ ...data.data, forIndex: index, type: 'issue' });
      } else {
        setFixError(data.error || 'Failed to generate fix commands');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error generating fix commands:', err);
      setFixError('Failed to generate fix commands');
    } finally {
      setAutoAddressingIndex(null);
    }
  };

  /**
   * Generate RouterOS commands for a plain-string recommendation via LLM
   */
  const handleApplyRecommendation = async (rec, index) => {
    setApplyingRecIndex(index);
    setFixError(null);
    setFixSuccess(null);
    const recommendationText = typeof rec === 'string' ? rec : (rec.title || rec.message || '');
    try {
      const response = await apiFetch(`/api/RouterConfig/${routerId}/generate-fix-commands`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: recommendationText,
          description: typeof rec !== 'string' ? (rec.description || '') : '',
          recommendation: recommendationText,
          category: 'general',
          severity: 'info'
        })
      });
      const data = await response.json();
      if (data.success) {
        setGeneratedFix({ ...data.data, forIndex: index, type: 'recommendation' });
      } else {
        setFixError(data.error || 'Failed to generate fix commands');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error generating fix commands:', err);
      setFixError('Failed to generate fix commands');
    } finally {
      setApplyingRecIndex(null);
    }
  };

  /**
   * Apply the generated fix commands to the live router (with safe mode + 2-min monitoring)
   */
  const handleApplyFix = async () => {
    if (!generatedFix) return;
    setApplyingFix(true);
    setFixError(null);
    setFixSuccess(null);
    try {
      const response = await apiFetch(`/api/RouterConfig/${routerId}/apply-fix-commands`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          commands: generatedFix.commands,
          description: generatedFix.explanation,
          useSafeMode: true
        })
      });
      const data = await response.json();
      if (data.success) {
        setFixSuccess(data.message || 'Commands applied successfully with safe mode monitoring.');
        setGeneratedFix(null);
      } else {
        setFixError(data.error || 'Failed to apply fix commands');
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error applying fix commands:', err);
      setFixError('Failed to apply fix commands');
    } finally {
      setApplyingFix(false);
    }
  };

  return (
    <div className="router-llm-validator">
      <div className="validator-header">
        <h3><FaRobot /> AI Configuration Review</h3>
        <p className="header-subtitle">
          Get AI-powered recommendations and security analysis for your router configurations
        </p>
      </div>

      {/* AI Disabled Message */}
      {!aiLoading && !aiEnabled && (
        <div className="alert alert-warning">
          <FaExclamationTriangle />
          <div>
            <strong>AI Insights Disabled</strong>
            <p>
              LLM-based configuration validation requires AI Insights to be enabled. 
              Please enable "AI Insights" in your <a href="/admin/settings">Settings</a> to use this feature.
            </p>
          </div>
        </div>
      )}

      {/* Loading AI Configuration */}
      {aiLoading && (
        <div className="alert alert-info">
          <FaSync className="fa-spin" /> Loading AI configuration...
        </div>
      )}

      {/* Show form only if AI is enabled */}
      {!aiLoading && aiEnabled && (
        <>
          {error && (
            <div className="alert alert-danger">
              <FaExclamationTriangle /> {sanitizeText(error)}
            </div>
          )}

      {/* Configuration Input Section */}
      <div className="input-section">
        <div className="input-header">
          <label htmlFor="config-input">Router Configuration</label>
          <div className="input-actions">
            {routerId && (
              <button
                className="btn btn-primary btn-sm"
                onClick={handleFetchAndValidate}
                disabled={fetchingConfig || loading}
                title={`Fetch live config from ${routerName || 'router'} and analyse`}
              >
                {fetchingConfig ? (
                  <><FaSync className="fa-spin" /> Fetching &amp; Analysing...</>
                ) : (
                  <><FaDownload /> Fetch &amp; Analyse</>
                )}
              </button>
            )}
            <button
              className="btn btn-secondary btn-sm"
              onClick={handlePasteFromClipboard}
              title="Paste from clipboard"
            >
              <FaClipboard /> Paste
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleClear}
              disabled={!configuration && !validationResult}
              title="Clear all"
            >
              <FaTimes /> Clear
            </button>
          </div>
        </div>
        
        <textarea
          id="config-input"
          className="config-textarea"
          placeholder="Paste your RouterOS configuration here...&#10;&#10;Example:&#10;/ip firewall filter&#10;add chain=input action=accept connection-state=established,related&#10;add chain=input action=drop&#10;&#10;/ip address&#10;add address=192.168.1.1/24 interface=ether1"
          value={configuration}
          onChange={(e) => setConfiguration(e.target.value)}
          disabled={loading}
          rows={15}
        />
        
        <div className="input-footer">
          <span className="char-count">
            {configuration.length} character{configuration.length !== 1 ? 's' : ''}
          </span>
          <button
            className="btn btn-primary"
            onClick={handleValidate}
            disabled={loading || !configuration.trim()}
          >
            {loading ? (
              <>
                <FaSync className="fa-spin" /> Analyzing...
              </>
            ) : (
              <>
                <FaRobot /> Review Configuration
              </>
            )}
          </button>
        </div>
      </div>

      {/* Validation Results Section */}
      {validationResult && (
        <div className="results-section">
          <div className="results-header">
            <h4>AI Review Results</h4>
            {validationResult.isValid ? (
              <span className="validation-badge valid">
                <FaCheckCircle /> Configuration Looks Good
              </span>
            ) : (
              <span className="validation-badge invalid">
                <FaExclamationTriangle /> Issues Found
              </span>
            )}
          </div>

          {/* Overall Summary */}
          {validationResult.summary && (
            <div className="summary-section">
              <h5>Summary</h5>
              <p>{sanitizeText(validationResult.summary)}</p>
            </div>
          )}

          {/* Issues */}
          {validationResult.issues && validationResult.issues.length > 0 && (
            <div className="issues-section">
              <h5>
                <FaExclamationTriangle /> Issues Found ({validationResult.issues.length})
              </h5>
              <div className="issues-list">
                {validationResult.issues.map((issue, index) => (
                  <div key={index} className={`issue-card ${getSeverityClass(issue.severity)}`}>
                    <div className="issue-header">
                      <span className="issue-severity">
                        {getSeverityIcon(issue.severity)}
                        {sanitizeText(issue.severity || 'Info')}
                      </span>
                      {issue.line && (
                        <span className="issue-line">Line {issue.line}</span>
                      )}
                    </div>
                    <div className="issue-title">
                      {sanitizeText(issue.title || issue.message)}
                    </div>
                    {issue.description && issue.description !== issue.title && (
                      <div className="issue-description">
                        {sanitizeText(issue.description)}
                      </div>
                    )}
                    {issue.recommendation && (
                      <div className="issue-recommendation">
                        <strong>Recommendation:</strong> {sanitizeText(issue.recommendation)}
                      </div>
                    )}
                    {routerId && (
                      <div className="issue-actions">
                        <button
                          className="btn btn-warning btn-sm"
                          onClick={() => handleAutoAddress(issue, index)}
                          disabled={autoAddressingIndex === index}
                          title="Generate and apply RouterOS fix commands (with safe mode)"
                        >
                          {autoAddressingIndex === index ? (
                            <><FaSync className="fa-spin" /> Generating Fix...</>
                          ) : (
                            <><FaTools /> Auto Address</>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {validationResult.recommendations && validationResult.recommendations.length > 0 && (
            <div className="recommendations-section">
              <h5>
                <FaLightbulb /> Recommendations ({validationResult.recommendations.length})
              </h5>
              <div className="recommendations-list">
                {validationResult.recommendations.map((rec, index) => (
                  <div key={index} className="recommendation-card">
                    <div className="recommendation-icon">
                      <FaLightbulb />
                    </div>
                    <div className="recommendation-content">
                      <div className="recommendation-title">
                        {typeof rec === 'string' ? sanitizeText(rec) : sanitizeText(rec.title || rec.message || '')}
                      </div>
                      {typeof rec !== 'string' && rec.description && rec.description !== rec.title && (
                        <div className="recommendation-description">
                          {sanitizeText(rec.description)}
                        </div>
                      )}
                      {typeof rec !== 'string' && rec.example && (
                        <div className="recommendation-example">
                          <strong>Example:</strong>
                          <pre>{sanitizeText(rec.example)}</pre>
                        </div>
                      )}
                      {routerId && (
                        <div className="recommendation-actions">
                          <button
                            className="btn btn-warning btn-sm"
                            onClick={() => handleApplyRecommendation(rec, index)}
                            disabled={applyingRecIndex === index}
                            title="Generate and apply RouterOS commands for this recommendation (with safe mode)"
                          >
                            {applyingRecIndex === index ? (
                              <><FaSync className="fa-spin" /> Generating...</>
                            ) : (
                              <><FaTools /> Apply</>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Best Practices */}
          {validationResult.bestPractices && validationResult.bestPractices.length > 0 && (
            <div className="best-practices-section">
              <h5>
                <FaCheckCircle /> Best Practices
              </h5>
              <ul className="best-practices-list">
                {validationResult.bestPractices.map((practice, index) => (
                  <li key={index}>
                    <FaCheckCircle className="check-icon" />
                    {sanitizeText(practice)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Security Concerns */}
          {validationResult.securityConcerns && validationResult.securityConcerns.length > 0 && (
            <div className="security-section">
              <h5>
                <FaExclamationTriangle /> Security Concerns
              </h5>
              <div className="security-list">
                {validationResult.securityConcerns.map((concern, index) => (
                  <div key={index} className="security-card">
                    <FaExclamationTriangle className="security-icon" />
                    <div className="security-content">
                      <div className="security-title">
                        {sanitizeText(concern.title || concern.message)}
                      </div>
                      {concern.description && (
                        <div className="security-description">
                          {sanitizeText(concern.description)}
                        </div>
                      )}
                      {concern.mitigation && (
                        <div className="security-mitigation">
                          <strong>Mitigation:</strong> {sanitizeText(concern.mitigation)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* LLM Provider Info */}
          {validationResult.provider && (
            <div className="provider-info">
              <FaInfoCircle /> Validated using {sanitizeText(validationResult.provider)}
              {validationResult.model && ` (${sanitizeText(validationResult.model)})`}
            </div>
          )}
        </div>
      )}

      {/* Fix error / success alerts */}
      {fixError && (
        <div className="alert alert-danger">
          <FaExclamationTriangle /> {sanitizeText(fixError)}
          <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setFixError(null)}><FaTimes /></button>
        </div>
      )}
      {fixSuccess && (
        <div className="alert alert-success">
          <FaCheckCircle /> {sanitizeText(fixSuccess)}
          <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setFixSuccess(null)}><FaTimes /></button>
        </div>
      )}

      {/* Generated Fix Modal */}
      {generatedFix && (
        <div className="fix-modal-overlay">
          <div className="fix-modal">
            <div className="fix-modal-header">
              <h4><FaTools /> Generated RouterOS Fix Commands</h4>
              <button className="btn btn-sm" onClick={() => setGeneratedFix(null)} disabled={applyingFix}>
                <FaTimes />
              </button>
            </div>
            <div className="fix-modal-body">
              <div className="alert alert-warning">
                <FaExclamationTriangle />
                <div>
                  <strong>⚠️ Live Router Operation</strong>
                  <p>These commands will be applied to the live router with Safe Mode enabled. The router will auto-revert if connectivity is lost within 5 minutes. A 2-minute monitoring period begins immediately after applying.</p>
                </div>
              </div>
              {generatedFix.explanation && (
                <div className="fix-explanation">
                  <h5>Explanation</h5>
                  <p>{sanitizeText(generatedFix.explanation)}</p>
                </div>
              )}
              {generatedFix.warnings && generatedFix.warnings.length > 0 && (
                <div className="alert alert-warning">
                  <FaExclamationTriangle />
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {generatedFix.warnings.map((w, i) => <li key={i}>{sanitizeText(w)}</li>)}
                  </ul>
                </div>
              )}
              <div className="fix-commands">
                <h5>Commands to Apply</h5>
                {generatedFix.commands && generatedFix.commands.length > 0 ? (
                  <pre className="commands-block">{generatedFix.commands.join('\n')}</pre>
                ) : (
                  <p className="no-commands">No commands generated. The LLM could not produce actionable commands for this issue.</p>
                )}
              </div>
            </div>
            <div className="fix-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setGeneratedFix(null)}
                disabled={applyingFix}
              >
                <FaTimes /> Cancel
              </button>
              <button
                className="btn btn-warning"
                onClick={handleApplyFix}
                disabled={applyingFix || !generatedFix.commands || generatedFix.commands.length === 0}
              >
                {applyingFix ? (
                  <><FaSync className="fa-spin" /> Applying...</>
                ) : (
                  <><FaTools /> Apply with Safe Mode</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Section - Always visible */}
      <div className="help-section">
        <h5><FaInfoCircle /> How AI Configuration Review Works</h5>
        <ul>
          {routerId && (
            <li><strong>Fetch &amp; Analyse:</strong> Click to automatically read the live config from {routerName || 'this router'} and analyse it — no pasting needed</li>
          )}
          <li>Or paste your RouterOS configuration script into the text area above</li>
          <li>Click "Review Configuration" to get AI-powered analysis</li>
          <li>The AI will review your config similar to how GitHub Copilot reviews code</li>
          <li>Review identified issues, security concerns, and improvement recommendations</li>
          <li>Each finding includes severity level and actionable advice</li>
          <li>The review uses the LLM provider configured in your AI settings</li>
          <li>For comprehensive analysis, provide complete configuration sections</li>
        </ul>
      </div>
      </>
      )}
    </div>
  );
};

export default RouterLLMValidator;
